import { Head } from 'vite-react-ssg';
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowRight, Cpu, Layers, Database, Zap, Gauge } from "lucide-react";
import Breadcrumbs from "@/components/Breadcrumbs";
import RelatedLinks from "@/components/RelatedLinks";

const DeepseekV4CompressedAttention = () => {
  const faqs = [
    {
      q: "What is DeepSeek V4 Compressed Attention?",
      a: "Compressed Attention is the umbrella name for the attention architecture inside DeepSeek-V4. It combines Compressed Sparse Attention (CSA), Heavily Compressed Attention (HCA) and low-rank query/output projections to shrink the KV-cache to roughly 2% of a standard transformer while keeping a 1M-token context window.",
    },
    {
      q: "How does DeepSeek V4 reduce KV-cache memory by ~98%?",
      a: "Instead of storing one Key-Value entry per token, DeepSeek V4 groups tokens (4 at a time for CSA, 128 at a time for HCA) and stores a single compressed entry per group. Combined with low-rank queries (~15.8% of original parameters) and grouped output projections (~14.3%), the cache footprint drops to about 2% of a vanilla transformer at the same sequence length.",
    },
    {
      q: "What is Compressed Sparse Attention (CSA)?",
      a: "CSA is a token-level compressor that merges small groups of tokens (typically 4) into one Key-Value entry using data-dependent per-dimension weighting. Overlapping windows are used between groups so information transitions smoothly instead of fragmenting at hard boundaries.",
    },
    {
      q: "What is Heavily Compressed Attention (HCA)?",
      a: "HCA compresses up to 128 tokens into a single KV entry, acting as a global summary of the sequence. It enables reasoning over very long contexts without exploding compute, at the cost of fine-grained local detail — which CSA layers handle instead.",
    },
    {
      q: "Why does DeepSeek V4 stack HCA, CSA and full attention in different layers?",
      a: "Early layers use HCA for cheap global context, middle layers alternate HCA and CSA to balance global and local information, and the final layer uses full uncompressed attention for maximum precision on the output. This hybrid stack is what allows V4 to keep state-of-the-art quality at a fraction of the memory.",
    },
    {
      q: "How does Compressed Attention compare to MLA in DeepSeek V3?",
      a: "DeepSeek V3 used Multi-head Latent Attention (MLA), which compresses across the head dimension. V4's Compressed Attention shifts the compression to the sequence dimension itself, which scales much better for million-token contexts where sequence length — not head count — is the dominant memory cost.",
    },
    {
      q: "Does Compressed Attention hurt model quality?",
      a: "On standard benchmarks DeepSeek V4 matches or beats prior open-source models. In real-world long-context tasks the quality is competitive, with the hybrid layer design ensuring the final precision layer is uncompressed. Trade-offs appear mainly in highly local fine-grained tasks dominated by HCA layers.",
    },
    {
      q: "Is DeepSeek V4 Compressed Attention open source?",
      a: "DeepSeek V4 Preview is released under the MIT license with open weights on HuggingFace and Ollama Cloud. The architectural techniques described here are documented in DeepSeek's published papers and code releases.",
    },
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    "headline": "DeepSeek V4 Compressed Attention: How the KV-Cache Shrinks to Just 2%",
    "datePublished": "2026-04-29T08:00:00+00:00",
    "dateModified": "2026-04-29T08:00:00+00:00",
    "author": { "@type": "Organization", "name": "The Independent DeepSeek Guide" },
    "publisher": {
      "@type": "Organization",
      "name": "The Independent DeepSeek Guide",
      "logo": { "@type": "ImageObject", "url": "https://deepseek.ai/favicon.svg" },
    },
    "description":
      "In-depth explainer of DeepSeek V4 Compressed Attention: CSA, HCA, low-rank queries and the hybrid layer stack that cut KV-cache memory to ~2% while supporting a 1M-token context.",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://deepseek.ai/blog/deepseek-v4-compressed-attention",
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };

  return (
    <>
      <Head>
        <title>DeepSeek V4 Compressed Attention — KV-Cache Explained</title>
        <meta
          name="description"
          content="DeepSeek V4 Compressed Attention explained: CSA, HCA, low-rank queries and how KV-cache drops to ~2% with a 1M-token context."
        />
        <meta
          name="keywords"
          content="DeepSeek V4, DeepSeek Compressed Attention, DeepSeek V4 KV cache, Compressed Sparse Attention, Heavily Compressed Attention, DeepSeek CSA, DeepSeek HCA, DeepSeek long context, DeepSeek 1M context, DeepSeek architecture"
        />
        <link rel="canonical" href="https://deepseek.ai/blog/deepseek-v4-compressed-attention" />
        <meta property="og:title" content="DeepSeek V4 Compressed Attention: KV-Cache 2% Explained" />
        <meta
          property="og:description"
          content="DeepSeek V4 cuts KV-cache memory to ~2%. A deep dive into CSA, HCA, low-rank queries and the hybrid layer stack powering 1M-token context."
        />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://deepseek.ai/blog/deepseek-v4-compressed-attention" />
        <meta property="og:image" content="https://deepseek.ai/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:image" content="https://deepseek.ai/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="DeepSeek V4 Compressed Attention: KV-Cache 2% Explained" />
        <meta
          name="twitter:description"
          content="CSA, HCA, low-rank queries and the hybrid layer stack inside DeepSeek V4. The architecture behind 1M-token context at ~2% memory."
        />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Head>

      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
          <Breadcrumbs currentPage="DeepSeek V4 Compressed Attention" />

          <article className="mt-6">
            <header className="mb-10">
              <div className="flex flex-wrap gap-2 mb-4">
                <Badge variant="secondary">AI Architecture</Badge>
                <Badge variant="outline">DeepSeek V4</Badge>
                <Badge variant="outline">KV-Cache 2%</Badge>
                <Badge variant="outline">1M Context</Badge>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-4 leading-tight">
                DeepSeek V4 Compressed Attention: How the KV-Cache Shrinks to Just 2%
              </h1>
              <p className="text-sm text-muted-foreground mb-4">April 29, 2026 · Independent DeepSeek Resource Hub</p>
              <p className="text-lg text-muted-foreground">
                DeepSeek V4 keeps a 1 million-token context window while using roughly{" "}
                <strong>2% of the KV-cache memory</strong> a standard transformer would need. The trick is a
                new architecture called <strong>Compressed Attention</strong> — a stack of CSA, HCA and
                low-rank projections that compresses along the sequence dimension instead of the head
                dimension.
              </p>
            </header>

            {/* TL;DR */}
            <Card className="mb-10 bg-primary/5 border-primary/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-primary" />
                  TL;DR — DeepSeek V4 Compressed Attention in 60 seconds
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-foreground/90">
                <p>• <strong>Problem:</strong> KV-cache memory grows linearly with sequence length and explodes at 1M tokens.</p>
                <p>• <strong>Fix:</strong> Compress along the sequence dimension instead of the head dimension.</p>
                <p>• <strong>CSA:</strong> Groups of ~4 tokens merged into one KV entry with data-dependent weighting and overlapping windows.</p>
                <p>• <strong>HCA:</strong> Up to 128 tokens compressed into one entry — a cheap global summary.</p>
                <p>• <strong>Low-rank:</strong> Queries shrunk to ~15.8% and output projections to ~14.3% of original size.</p>
                <p>• <strong>Hybrid stack:</strong> HCA early, mixed middle, full attention at the final layer for precision.</p>
                <p>• <strong>Result:</strong> ~2% of the original KV-cache footprint, state-of-the-art quality.</p>
              </CardContent>
            </Card>

            {/* The Problem */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 flex items-center gap-2">
                <Database className="h-7 w-7 text-primary" />
                The Problem: KV-Cache Memory Explodes at Long Context
              </h2>
              <p className="text-foreground/90 mb-4">
                In a standard Transformer, every generated token has to attend to every previous token. To
                avoid recomputing, the model caches the Key and Value vectors for every past token — the{" "}
                <strong>KV-cache</strong>. That cache grows <em>linearly</em> with sequence length and{" "}
                <em>linearly</em> with the number of attention heads.
              </p>
              <p className="text-foreground/90 mb-4">
                At 1 million tokens — DeepSeek V4's native context window — a vanilla KV-cache becomes the
                single biggest memory bottleneck on inference hardware. Earlier DeepSeek work (V2, V3) tackled
                this with <strong>Multi-head Latent Attention (MLA)</strong>, compressing across the{" "}
                <em>head</em> dimension. V4 takes a fundamentally different route: compress along the{" "}
                <strong>sequence dimension</strong> itself, where the real growth happens.
              </p>
            </section>

            {/* CSA */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 flex items-center gap-2">
                <Layers className="h-7 w-7 text-primary" />
                Compressed Sparse Attention (CSA)
              </h2>
              <p className="text-foreground/90 mb-4">
                CSA is the <strong>fine-grained compressor</strong> in DeepSeek V4. Instead of storing one KV
                entry per token, it groups small windows of tokens — typically four — into a single
                compressed Key-Value pair.
              </p>

              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Token-level compressor</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm text-foreground/80">
                    Groups of ~4 tokens are merged into a single KV entry, instantly cutting cache size 4×
                    before any other optimisation kicks in.
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Data-dependent weighting</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm text-foreground/80">
                    Rather than averaging, the model learns <em>per dimension</em> which information matters
                    most to keep — so important features survive compression.
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Overlapping windows</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm text-foreground/80">
                    Groups overlap instead of butting up against each other, preventing hard boundaries and
                    keeping information flow smooth across compressed entries.
                  </CardContent>
                </Card>
              </div>

              <p className="text-foreground/90">
                The net effect: CSA preserves the <em>local</em> resolution the model needs for precise
                next-token prediction, while still cutting the cache to a fraction of the original.
              </p>
            </section>

            {/* HCA */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 flex items-center gap-2">
                <Gauge className="h-7 w-7 text-primary" />
                Heavily Compressed Attention (HCA)
              </h2>
              <p className="text-foreground/90 mb-4">
                For the truly long stretches of context, DeepSeek V4 falls back to <strong>HCA</strong> — a
                much more aggressive compressor that bundles up to <strong>128 tokens into a single KV
                entry</strong>.
              </p>
              <p className="text-foreground/90 mb-4">
                HCA acts as a <em>global summary</em>. Individual fine-grained details are sacrificed, but
                the model can suddenly reason over enormous spans of text without compute or memory blowing
                up. In practice, HCA layers behave like a "long-range index" that the precise CSA and full
                attention layers can refer back to.
              </p>
            </section>

            {/* Low-rank */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4">Low-Rank Queries &amp; Grouped Output Projection</h2>
              <p className="text-foreground/90 mb-6">
                Compressing the KV-cache solves memory. To also cut <em>compute</em>, DeepSeek V4 shrinks the
                projection matrices themselves:
              </p>

              <div className="overflow-x-auto mb-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Component</TableHead>
                      <TableHead>What gets shrunk</TableHead>
                      <TableHead>Approx. size vs. baseline</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">Low-rank queries</TableCell>
                      <TableCell>Query projection matrices factorised into two smaller matrices.</TableCell>
                      <TableCell><Badge variant="secondary">~15.8%</Badge></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Grouped output projection</TableCell>
                      <TableCell>Output projection shared across grouped heads.</TableCell>
                      <TableCell><Badge variant="secondary">~14.3%</Badge></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">KV-cache (CSA + HCA)</TableCell>
                      <TableCell>Per-group compressed entries instead of per-token.</TableCell>
                      <TableCell><Badge>~2%</Badge></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
              <p className="text-xs text-muted-foreground">
                Figures based on the architecture summary in Jia-Bin Huang's DeepSeek V4 explainer.
              </p>
            </section>

            {/* Hybrid stack */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 flex items-center gap-2">
                <Cpu className="h-7 w-7 text-primary" />
                The Hybrid Layer Stack
              </h2>
              <p className="text-foreground/90 mb-4">
                The real cleverness of Compressed Attention is <em>where</em> each variant gets used. DeepSeek
                V4 doesn't apply one technique uniformly — it stacks them by depth:
              </p>

              <div className="space-y-3 mb-6">
                <Card className="border-l-4 border-l-primary">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Early layers — HCA</CardTitle>
                    <CardDescription>Cheap global summary of the entire context window.</CardDescription>
                  </CardHeader>
                </Card>
                <Card className="border-l-4 border-l-primary/60">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Middle layers — alternating HCA and CSA</CardTitle>
                    <CardDescription>Balance between long-range overview and local detail.</CardDescription>
                  </CardHeader>
                </Card>
                <Card className="border-l-4 border-l-primary/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Final layer — full uncompressed attention</CardTitle>
                    <CardDescription>Maximum precision for the output token, no information loss.</CardDescription>
                  </CardHeader>
                </Card>
              </div>

              <p className="text-foreground/90">
                Because the final layer is uncompressed, the model still gets a clean, high-resolution view
                when it actually generates the next token. The expensive compute is concentrated where it
                matters most.
              </p>
            </section>

            {/* Why it matters */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4">Why Compressed Attention Matters</h2>
              <ul className="list-disc pl-6 space-y-2 text-foreground/90">
                <li>
                  <strong>Million-token context becomes practical.</strong> Memory is no longer the wall —
                  hardware that previously couldn't host long-context inference now can.
                </li>
                <li>
                  <strong>Inference cost collapses.</strong> Smaller KV-cache + smaller projections means
                  cheaper serving, which directly enables DeepSeek V4 Flash's $0.14 / 1M input pricing.
                </li>
                <li>
                  <strong>Open-source impact.</strong> With MIT-licensed weights, the entire community can
                  inspect and reuse the Compressed Attention design.
                </li>
                <li>
                  <strong>Architecture trend.</strong> Sequence-dimension compression is likely to become
                  standard in the next wave of long-context models.
                </li>
              </ul>
            </section>

            {/* FAQ */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-6">Frequently Asked Questions</h2>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`}>
                    <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-foreground/85">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>

            {/* CTA */}
            <section className="mb-12 text-center bg-muted/40 rounded-lg p-8">
              <h2 className="text-2xl font-bold mb-3">Try DeepSeek V4 in your browser</h2>
              <p className="text-muted-foreground mb-6">
                Access DeepSeek V4 Pro and Flash directly from any webpage with the AI Sidebar Chrome
                Extension.
              </p>
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90">
                <a
                  href="https://chromewebstore.google.com/detail/ai-sidebar-with-deepseek/inhcgfpbfdjbjogdfjbclgolkmhnooop"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Install AI Sidebar <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </section>

            {/* Related */}
            <section className="border-t pt-8">
              <h2 className="text-2xl font-bold mb-6">Related Reading</h2>
              <div className="flex flex-wrap gap-3 mb-6">
                <Link to="/deepseek-v4-flash-review" className="text-primary hover:underline">DeepSeek V4 Flash Review →</Link>
                <Link to="/blog/deepseek-v4-next-move" className="text-primary hover:underline">DeepSeek's V4 Next Move →</Link>
                <Link to="/blog/deepseek-engram-v4-architecture" className="text-primary hover:underline">DeepSeek Engram Architecture →</Link>
                <Link to="/deepseek-v4" className="text-primary hover:underline">DeepSeek V4 Overview →</Link>
              </div>
              <RelatedLinks />
            </section>
          </article>
        </div>
      </div>
    </>
  );
};

export default DeepseekV4CompressedAttention;
